/**
 * PlaybackEngine
 * Standalone canvas-based lightshow playback engine.
 * Adapted from app CanvasPlaybackEngine — no Meteor dependencies.
 */

import { buildHelpers } from './canvasHelpers';

const MAX_SLOW_FRAMES = 3;
const SLOW_FRAME_THRESHOLD_MS = 100;

export class PlaybackEngine {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.compiledFn = null;
    this.code = null;
    this.duration = 0;
    this.startTime = null;
    // Raw server start timestamp (before the offset is applied). Kept so the
    // start time can be recomputed from a fresher offset at playback start.
    this.rawStartTime = null;
    this.syncOffset = 0;
    // Optional () => number provider for the LIVE clock offset (#319 Lever A).
    this.getSyncOffset = null;
    this.zoneIndex = null;
    this.zoneCount = 0;
    this.zoneOptions = [];
    this.rowIndex = null;
    this.params = {};
    this.status = 'idle';
    this.rafId = null;
    this.callbacks = {
      onStart: null,
      onComplete: null,
      onStopped: null,
      onError: null,
      // Per-frame hook, given the SAME synced t the script renders from. Used
      // for sound cues, so audio and light cannot drift apart (MM #116).
      onFrame: null,
    };
    this.slowFrameCount = 0; // consecutive slow frames (resets on a good frame)
    this.totalSlowFrames = 0; // cumulative — drives playbackAccuracy telemetry
    this.frameCount = 0;
    this._waitTimer = null;
  }

  setCanvas(canvasElement) {
    this.canvas = canvasElement;
    this.ctx = canvasElement ? canvasElement.getContext('2d') : null;
  }

  /**
   * Initialize engine with script code and timing.
   * @param {string} code - Script function body
   * @param {number} duration - Duration in ms
   * @param {number} startTime - Unix timestamp (ms) when playback should start
   * @param {number} syncOffset - Time offset from TimeSync
   * @param {number|null} zoneIndex
   * @param {number} zoneCount
   * @param {Object} params - Template param values
   * @returns {{ ready: true } | { error: string }}
   */
  initialize(code, duration, startTime, syncOffset = 0, zoneIndex = null, zoneCount = 0, params = {}, rowIndex = null, zoneOptions = []) {
    try {
      if (!code || typeof code !== 'string') {
        return { error: 'Code is required' };
      }
      if (!duration || typeof duration !== 'number' || duration <= 0) {
        return { error: 'Duration must be a positive number' };
      }
      if (!startTime || typeof startTime !== 'number') {
        return { error: 'Start time must be a valid timestamp' };
      }

      try {
        // eslint-disable-next-line no-new-func
        this.compiledFn = new Function('ctx', 't', 'duration', 'helpers', code);
      } catch (compileError) {
        return { error: `Failed to compile script: ${compileError.message}` };
      }

      const adjustedStartTime = startTime - syncOffset;
      const now = Date.now();

      // Only refuse if the show has actually ENDED. A client that arms/joins a
      // few seconds (or minutes) into a running show should pick up mid-show at
      // t=elapsed, not be rejected — _tick() renders from elapsed and completes
      // once elapsed >= duration.
      if (now - adjustedStartTime >= duration) {
        return { error: 'Show already ended' };
      }

      this.code = code;
      this.duration = duration;
      this.startTime = adjustedStartTime;
      this.rawStartTime = startTime;
      this.syncOffset = syncOffset;
      this.zoneIndex = zoneIndex;
      this.zoneCount = zoneCount;
      this.rowIndex = rowIndex;
      this.zoneOptions = zoneOptions;
      this.params = params;
      this.status = 'initialized';
      this.slowFrameCount = 0;
      this.totalSlowFrames = 0;
      this.frameCount = 0;

      return { ready: true };
    } catch (error) {
      return { error: error.message || 'Failed to initialize playback engine' };
    }
  }

  start() {
    if (!this.compiledFn) return { error: 'Not initialized' };
    if (!this.canvas || !this.ctx) return { error: 'Canvas not set' };
    if (this.status === 'playing') return { error: 'Already playing' };

    const now = Date.now();
    const delay = this.startTime - now;

    // Join mid-show: only refuse if the show is already over. If the start time
    // is in the past but the show is still running (delay <= 0, not yet ended),
    // begin playback immediately at t=elapsed instead of rejecting.
    if (now - this.startTime >= this.duration) return { error: 'Show already ended' };

    this.status = 'scheduled';

    if (delay > 0) {
      this._waitTimer = setTimeout(() => this._beginPlayback(), delay);
    } else {
      this._beginPlayback();
    }

    return { playing: true };
  }

  _beginPlayback() {
    // #319 Lever A: adopt the FRESHEST clock offset at the actual show start.
    // initialize() may have run seconds/minutes earlier (the client armed
    // early); TimeSync keeps re-syncing and usually improves in the meantime.
    // Using the init-time offset would lock a stale — possibly bad-join —
    // estimate in for the whole show, which is what fattens the cross-device
    // p95 tail. One-time here (not per frame) → no mid-show jump.
    if (this.getSyncOffset) {
      const fresh = this.getSyncOffset();
      if (Number.isFinite(fresh) && this.rawStartTime != null) {
        this.syncOffset = fresh;
        this.startTime = this.rawStartTime - fresh;
      }
    }
    this.status = 'playing';
    if (this.callbacks.onStart) this.callbacks.onStart();
    this._tick();
  }

  /**
   * Provide a live clock-offset getter, adopted once at playback start.
   * @param {() => number} fn
   */
  setSyncOffsetProvider(fn) {
    this.getSyncOffset = fn;
  }

  _tick() {
    if (this.status !== 'playing') return;

    const now = Date.now();
    const elapsed = now - this.startTime;

    if (elapsed >= this.duration) {
      this._complete();
      return;
    }

    const tSeconds = Math.max(0, elapsed) / 1000;
    const durationSeconds = this.duration / 1000;
    const helpers = buildHelpers(this.canvas, this.zoneIndex, this.zoneCount, this.params, this.rowIndex, this.zoneOptions);
    // This client is always a phone. Set explicitly so scripts can rely on the
    // flag existing rather than testing for undefined.
    helpers.isWall = false;

    const frameStart = performance.now();
    try {
      this.compiledFn(this.ctx, tSeconds, durationSeconds, helpers);
    } catch {
      this.ctx.fillStyle = '#000000';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }
    const frameDuration = performance.now() - frameStart;

    // After the draw, so a throwing cue handler can never cost a frame.
    if (this.callbacks.onFrame) {
      try { this.callbacks.onFrame(tSeconds); } catch { /* never break playback */ }
    }

    this.frameCount++;

    if (frameDuration > SLOW_FRAME_THRESHOLD_MS) {
      this.slowFrameCount++;
      this.totalSlowFrames++;
      if (this.slowFrameCount >= MAX_SLOW_FRAMES) {
        this._fillBlack();
        this.status = 'error';
        if (this.callbacks.onError) {
          this.callbacks.onError(new Error('Script too slow'));
        }
        return;
      }
    } else {
      this.slowFrameCount = 0;
    }

    this.rafId = requestAnimationFrame(() => this._tick());
  }

  _fillBlack() {
    if (this.ctx && this.canvas) {
      this.ctx.fillStyle = '#000000';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  _complete() {
    if (this.status !== 'playing') return;
    this.status = 'completed';
    this._cancelRAF();
    if (this.callbacks.onComplete) this.callbacks.onComplete();
  }

  stop() {
    const wasPlaying = this.status === 'playing' || this.status === 'scheduled';
    this.status = 'stopped';
    this._cancelRAF();
    if (this._waitTimer) {
      clearTimeout(this._waitTimer);
      this._waitTimer = null;
    }
    if (wasPlaying && this.callbacks.onStopped) this.callbacks.onStopped();
  }

  _cancelRAF() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  getStatus() {
    const now = Date.now();
    let elapsedTime = 0;
    if (this.status === 'playing' && this.startTime) {
      elapsedTime = Math.max(0, now - this.startTime);
    }
    return {
      status: this.status,
      elapsedTime,
      startTime: this.startTime,
      frameCount: this.frameCount,
      duration: this.duration,
    };
  }

  onStart(callback) { this.callbacks.onStart = callback; }
  onFrame(callback) { this.callbacks.onFrame = callback; }
  onComplete(callback) { this.callbacks.onComplete = callback; }
  onStopped(callback) { this.callbacks.onStopped = callback; }
  onError(callback) { this.callbacks.onError = callback; }

  getTelemetry() {
    return {
      status: this.status,
      framesRendered: this.frameCount,
      duration: this.duration,
      startTime: this.startTime,
      syncOffset: this.syncOffset,
      // Smoothness % — share of frames rendered under the slow threshold.
      // Populates the aggregate's avgPlaybackAccuracy (previously always null).
      playbackAccuracy:
        this.frameCount > 0
          ? Math.round((1 - this.totalSlowFrames / this.frameCount) * 100)
          : null,
    };
  }

  reset() {
    this.stop();
    this.canvas = null;
    this.ctx = null;
    this.compiledFn = null;
    this.code = null;
    this.duration = 0;
    this.startTime = null;
    this.rawStartTime = null;
    this.syncOffset = 0;
    this.getSyncOffset = null;
    this.zoneIndex = null;
    this.zoneCount = 0;
    this.zoneOptions = [];
    this.rowIndex = null;
    this.params = {};
    this.status = 'idle';
    this.slowFrameCount = 0;
    this.totalSlowFrames = 0;
    this.frameCount = 0;
    this.callbacks = {
      onStart: null,
      onComplete: null,
      onStopped: null,
      onError: null,
    };
  }
}
